export interface ChatMessage {
  id: string | number;
  isUser: boolean;
  createdAt: string;
  isTempMsg?: boolean;
}

export interface DividerResult {
  firstUnreadId: string | null;
  count: number;
}

/**
 * Deterministically locate the first unread message and count unreads.
 *
 * Primary path  — uses lastReadAt timestamp (single source of truth).
 * Fallback path — index math when lastReadAt is unavailable (first session).
 *
 * Only messages from others (!isUser) count as unread for the viewer.
 */
export function computeDivider(
  msgs: ChatMessage[],
  lastReadAt: string | null,
  fallbackCount: number,
): DividerResult {
  if (!msgs.length) return { firstUnreadId: null, count: 0 };

  if (lastReadAt) {
    const threshold = new Date(lastReadAt).getTime();
    const firstIdx = msgs.findIndex(
      (m) => !m.isUser && new Date(m.createdAt).getTime() > threshold,
    );
    if (firstIdx === -1) return { firstUnreadId: null, count: 0 };
    const count = msgs.slice(firstIdx).filter((m) => !m.isUser).length;
    return { firstUnreadId: String(msgs[firstIdx].id), count };
  }

  if (fallbackCount > 0) {
    const idx = Math.max(0, msgs.length - fallbackCount);
    const first = msgs[idx];
    return first?.id
      ? { firstUnreadId: String(first.id), count: fallbackCount }
      : { firstUnreadId: null, count: 0 };
  }

  return { firstUnreadId: null, count: 0 };
}
